/**
 * `pageStartUnits` — DOM-measuring glue for `pageBreakModel` (RECUR-27304
 * Delta v3). Moved out of `usePageBoundaries.ts` so the SAME model output can
 * be consumed by BOTH the always-mounted overlay hook AND the server exporter
 * (`exportToPdfServer.ts`) without pulling React into the export bundle — NO
 * React import anywhere in this file.
 *
 * `pageBreakModel` returns page-start UNIT INDICES (flat atomic-unit
 * positions — one per top-level block, or one per rowspan-bound row GROUP
 * for a decomposed table (ordinarily one `<tr>` each — RECUR-27304
 * rowspan-safe pagination, see `computeRowGroups`); see `pageBreakModel.ts` /
 * `reindexGroupsForRows`). The private
 * `computePageStartIndices` runs that model once; this module projects its
 * ONE output two ways:
 *   • `computePageBoundaries(blocks, width)` -> the overlay's existing shape
 *     `{ boundaries: number[] (Ys), pageHeightPx }` — byte-identical output
 *     to the pre-Delta-v3 `usePageBoundaries.computePageBoundaries` (maps
 *     each start index to `geometry[i].offsetTop`).
 *   • `computePageStartUnits(contentEl)` -> the LIVE element(s) each
 *     page-start unit corresponds to, for `exportToPdfServer.ts` to mark with
 *     `data-pdf-page-break` (whole-block units) or split a cloned `<table>`
 *     at (row units) — see `PageStartUnit` below.
 *
 * Delta v4 (gap-display, RECUR-27304): this file works in TWO coordinate
 * spaces and every value below is documented as to which one it's in —
 * mixing them up is exactly the measurement-feedback-loop bug this delta
 * exists to avoid (a rendered gap shifts `offsetTop`, which would otherwise
 * shift the next computed gap, forever):
 *   • NORMALIZED (gap-free) — what `geometry` (fed to `pageBreakModel`) and
 *     `computePageStartUnits`' output are in. The CURRENT cumulative gap
 *     already painted on the live DOM (read back via `getInjectedGapPx`,
 *     never tracked as separate module state) is subtracted back out of
 *     every unit's `offsetTop` before the model ever sees it, so the model
 *     always reasons about the SAME layout it would if no gap had ever been
 *     applied — this is what makes re-running the whole pipeline on
 *     already-gapped geometry idempotent instead of oscillating, and is
 *     also why `computePageStartUnits` (the export projection) stays
 *     completely unaffected by whether `showPageGaps` happens to be on.
 *   • LIVE (post-gap) — what `computePageBoundaries`' `boundaries` are in:
 *     the ACTUAL on-screen Y, gap included, so `PageBoundaryOverlay`'s seam
 *     + chip land at the true start of the new page (right after the gap).
 */
import { getServerPageHeightPx } from './pageGeometry';
import { buildPaginationGroups } from './exportToPdf';
import { pageBreakModelWithGaps, reindexGroupsForRowsAndLines, LINE_ORPHAN_WIDOW_MIN_LINES, type BlockGeometry } from './pageBreakModel';

export interface PageBoundaries {
    /** Y position (CSS px, LIVE/post-gap coordinates — see module doc) of each interior page cut. */
    boundaries: number[];
    /**
     * Unused space (CSS px) left behind on the page ABOVE `boundaries[i]`
     * (RECUR-27304 gap-display) — parallel to `boundaries`. 0 when the
     * group simply fit with nothing to spare, AND for a mid-table 'row'
     * start: margin has no effect on a `<tr>`, so `applyPageGapStyles` never
     * renders a real gap there even though the model's own math is exact —
     * see `computePageGapTargets`, which is what actually decides what gets
     * painted in the DOM.
     */
    gaps: number[];
    /**
     * Page-start kind per boundary, parallel to `boundaries`/`gaps`
     * (RECUR-27304 Word-frame visuals) — mirrors `PageStartUnit.kind`. Only a
     * `'block'`-kind boundary has a live element margin can attach to, so
     * only it can ever receive a real DOM gap via `computePageGapTargets` /
     * `applyPageGapStyles`; a `'row'`/`'line'`-kind boundary (mid-table /
     * mid-paragraph split) never gets margin (CSS margin has no effect on a
     * `<tr>`, and a mid-paragraph split has no block-level box to attach to
     * either). `PageBoundaryOverlay` itself no longer reads this field
     * (RECUR-27304 "one consistent marker") — it renders the identical
     * seam + chip at every boundary regardless of kind.
     */
    kinds: Array<'block' | 'row' | 'line'>;
    /** The SERVER page height boundaries were computed against (0 when width isn't measurable). */
    pageHeightPx: number;
}

/**
 * One page-start decision, mapped back onto the LIVE DOM element(s) it
 * corresponds to (RECUR-27304 Delta v3 — the export's injection unit).
 *   • `kind === 'block'` — `element` is the live top-level block that begins
 *     the page (a whole-block start, including a table whose FIRST row
 *     begins the page — nothing to split there, the whole table just moves).
 *     `blockIndex` is its position in `getTopLevelBlocks(contentEl)`; the
 *     exporter re-resolves that SAME position against `getTopLevelBlocks(clone)`
 *     (`cloneNode` preserves order 1:1) and marks the CLONE's block with
 *     `data-pdf-page-break` — the live element is never touched.
 *   • `kind === 'row'` — a MID-TABLE start: `tableBlock` is the live
 *     top-level block that IS/wraps the `<table>` (the same shape
 *     `getTopLevelBlocks` returns), `rowIndex` (always >= 1 — row 0 can only
 *     ever be part of a `'block'` start) is the FIRST `<tr>` index of the
 *     rowspan-bound row GROUP the break falls before (RECUR-27304
 *     rowspan-safe pagination — see `computeRowGroups`; ordinarily just the
 *     row itself for a plain table with no rowSpan) — the only row index a
 *     break may ever land on, so a spanning cell is never split across the
 *     export's physical table slice. `element` is that row's own `<tr>`. The
 *     exporter physically SPLITS the CLONED table there — it never marks a
 *     live `<tr>`. `blockIndex` is unset for row-kind units —
 *     `splitClonedTablesAtRows` matches the cloned table by element identity
 *     via `tableBlock`, not by index.
 *   • `kind === 'line'` — a MID-PARAGRAPH start (RECUR-27304 Delta v5, the
 *     paragraph analogue of `'row'`): `textBlock` is the live top-level `<p>`
 *     the break falls inside, `lineIndex` is the (0-based) rendered line the
 *     break falls BEFORE — by construction of `reindexGroupsForRowsAndLines`'
 *     orphan/widow grouping this is always >= 2 (line 0 is a `'block'`-kind
 *     start — "the whole paragraph moves", nothing to split there — and line
 *     1 can never be any group's OWN start). `element` is the SAME live `<p>`
 *     (there is no single DOM node representing "just this line" the way a
 *     `<tr>` represents a row). The exporter physically SPLITS the CLONED
 *     paragraph there (`splitClonedParagraphsAtLines`) — it never marks or
 *     mutates the live `<p>`. `blockIndex` is unset, same reasoning as `'row'`.
 */
export interface PageStartUnit {
    element: HTMLElement;
    offsetTop: number;
    kind: 'block' | 'row' | 'line';
    tableBlock?: HTMLElement;
    rowIndex?: number;
    /** Live top-level `<p>` a `'line'`-kind start falls inside — see `PageStartUnit`'s own doc. */
    textBlock?: HTMLElement;
    /** 0-based rendered line index a `'line'`-kind start falls BEFORE — see `PageStartUnit`'s own doc. */
    lineIndex?: number;
    /** Position in `getTopLevelBlocks(contentEl)` — set only for `kind === 'block'`. */
    blockIndex?: number;
}

export const EMPTY_BOUNDARIES: PageBoundaries = { boundaries: [], gaps: [], kinds: [], pageHeightPx: 0 };

/**
 * One gap-application target for the live editor DOM (RECUR-27304
 * gap-display): a top-level block that starts a new page, paired with the
 * vertical space to render immediately above it. Only ever `'block'`-kind
 * page-starts (see `PageStartUnit.kind`) appear here — a mid-table `'row'`
 * start has no live top-level element margin can attach to (CSS margin does
 * not apply to `<tr>`), so `computePageGapTargets` never emits one for it.
 * Consumed by `usePageBoundaries`'s `applyPageGapStyles`; kept separate from
 * `computePageBoundaries` (a pure Y/gap projection with no element
 * identities) and from `computePageStartUnits` (the untouched export
 * projection).
 */
export interface PageGapTarget {
    element: HTMLElement;
    gapPx: number;
}

/**
 * The CSS custom property `applyPageGapStyles` writes the injected gap onto
 * a page-start block with, and this module reads back to NORMALIZE live
 * geometry (see the module doc's coordinate-space note). Self-describing by
 * design — the DOM itself is the only source of truth for "how much gap is
 * currently painted where", so there is no separate mutable state that could
 * ever drift from what's actually rendered.
 */
export const PAGE_GAP_CSS_VAR = '--page-gap';

/** Reads back the gap (CSS px) currently painted on `el` via `PAGE_GAP_CSS_VAR`, or 0 if none. */
function getInjectedGapPx(el: HTMLElement): number {
    const px = parseFloat(el.style.getPropertyValue(PAGE_GAP_CSS_VAR));
    return Number.isFinite(px) && px > 0 ? px : 0;
}

/** `true` for the exact element `TableElement` renders as a Slate table node. */
function isSlateTableElement(el: Element): el is HTMLTableElement {
    return el.tagName === 'TABLE' && el.getAttribute('data-slate-node') === 'element';
}

/**
 * Finds the `<table>` a top-level block either IS or WRAPS. `TableElement`
 * (tableElements.tsx) renders every table inside a plain `.tableScroll` div
 * (RECUR-26175's horizontal-scroll wrapper for wide pastes) — that div, NOT
 * the `<table>` itself, is what sits directly under `plate-editor-content`
 * (the div carries none of Slate's own attributes; the table one level
 * inside it does). Checking the block's own tag first keeps this correct if
 * a future change ever renders the table without that wrapper.
 *
 * Deliberately scoped to a DIRECT child, or a table that's a direct child of
 * ONE direct-child wrapper (the `.tableScroll` shape above) — never an
 * unbounded-depth descendant search. A composite top-level block (column,
 * callout, blockquote — nested tables are open tech debt, see recur-wiki)
 * can CONTAIN a table several levels down without that table being the
 * block's own top-level table; an unscoped `querySelector` would match it
 * anyway and this function's callers would then decompose the WHOLE
 * composite block row-by-row against that unrelated nested table's geometry.
 *
 * Walked explicitly (not a `:scope`-combinator `querySelector`) — jsdom's
 * `:scope` scoping is unreliable on a DETACHED subtree (matches at any depth
 * instead of respecting the `>` combinator), which is exactly how this
 * module's own tests (and the exporter's) build every fixture; explicit
 * child-walking is correct regardless of whether `block` is attached.
 */
export function findTableElement(block: HTMLElement): HTMLTableElement | null {
    if (block.tagName === 'TABLE') return block as unknown as HTMLTableElement;
    for (const child of Array.from(block.children)) {
        if (isSlateTableElement(child)) return child;
        if (child.tagName === 'TABLE') continue; // never descend into a nested table's own cells
        for (const grandchild of Array.from(child.children)) {
            if (isSlateTableElement(grandchild)) return grandchild;
        }
    }
    return null;
}

/**
 * The same top-level-block filter `planPages` uses (exportToPdf.ts:606-608),
 * WIDENED for tables: a `.tableScroll`-wrapped table's `<table
 * data-slate-node="element">` lives ONE level inside `contentEl`'s direct
 * child (see `findTableElement`), so the bare `data-slate-node==='element'`
 * check alone never matches the wrapper — every table in the document would
 * otherwise be invisible here, contributing zero height to the model. A
 * top-level element counts as a block when it EITHER carries
 * `data-slate-node="element"` itself (every other block type, and a table
 * that somehow renders unwrapped) OR wraps one.
 */
export function getTopLevelBlocks(contentEl: HTMLElement): HTMLElement[] {
    return Array.from(contentEl.children).filter((child): child is HTMLElement => {
        if (!(child instanceof HTMLElement)) return false;
        if (child.getAttribute('data-slate-node') === 'element') return true;
        return findTableElement(child) !== null;
    });
}

/**
 * Cumulative `.offsetTop` of `el` relative to `ancestor`, walking the
 * `offsetParent` chain instead of trusting a single `.offsetTop` read. A
 * `<tr>`'s `offsetParent` stops AT its `<table>` — the CSSOM `offsetParent`
 * algorithm special-cases `td`/`th`/`table` as a boundary regardless of
 * position — so `row.offsetTop` alone is relative to the TABLE, not to
 * `plate-editor-content` like every other top-level block's `offsetTop`
 * already is. Summing the chain restores a value in that same shared space.
 * Falls back to 0 extra if the chain never reaches `ancestor` (defensive —
 * shouldn't happen for a row that's actually inside the measured table).
 */
function cumulativeOffsetTop(el: HTMLElement, ancestor: HTMLElement): number {
    let top = 0;
    let node: HTMLElement | null = el;
    while (node && node !== ancestor) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
    }
    return top;
}

// ── Table row-group decomposition (RECUR-27304 rowspan-safe pagination) ─────
//
// A `rowSpan=N` cell drawn in row `i` visually occupies rows `[i, i+N-1]`,
// even though it lives in ONE `<tr>` (row `i`'s own). Treating every `<tr>`
// as an independently-breakable unit (the pre-fix decomposition below) let
// the model choose a break INSIDE that span; the export then physically
// slices the table at that row (`splitOneTableAtRows`, exportToPdfServer.ts),
// stranding the spanning cell in whichever half kept its origin `<tr>` — the
// corruption this section exists to fix. Headless Chromium's own native
// fragmentation already treats a rowspan group as atomic (verified via a
// real render, RECUR-27304): it only ever seams BETWEEN such groups, never
// inside one. `computeRowGroups` makes this model do the same —
// `buildAtomicUnits` below decomposes a table into one atomic unit per GROUP
// instead of per `<tr>`; a table with no `rowSpan` anywhere degenerates to
// one group per row, so this is byte-identical to the old per-`<tr>`
// decomposition for every plain table.

/**
 * One maximal run of a table's `<tr>` indices bound together by any
 * `rowSpan`, both ends inclusive — see this section's own module comment for
 * why a page break may only ever land at a group's own `start`, never inside
 * one.
 */
export interface TableRowGroup {
    /** Index into `table.rows` of this group's first row (inclusive) — the ONLY row index a page-break may ever land on. */
    start: number;
    /** Index into `table.rows` of this group's last row (inclusive) — the furthest row any rowSpan opened within the group still reaches. */
    end: number;
}

/**
 * Groups `rows` (a table's `<tr>`s, or an equivalent `ArrayLike` in tests)
 * into maximal `TableRowGroup` runs — a standard interval-merge: `reach`
 * tracks the furthest row any cell seen so far in the CURRENT group demands
 * (`rowIndex + rowSpan - 1`), and a group closes at row `i` exactly when
 * `i === reach` — nothing still open extends past it. `reach = Math.max(reach, i)`
 * floors `reach` at the row just processed on every iteration: a `<tr>`
 * fully COVERED by an earlier row's rowSpan contributes no `<td>`/`<th>` of
 * its OWN here (the DOM never lists a covered slot as a child of the row it
 * merely passes through), so without the floor a covered row would read a
 * stale `reach` left over from before the group even started. A table with
 * NO `rowSpan` anywhere degenerates to one group PER ROW (every cell's own
 * span is 1, so `reach` always lands on the current row) — byte-identical to
 * treating every `<tr>` as its own atomic unit, i.e. this codebase's pre-fix
 * behaviour for every plain table.
 */
export function computeRowGroups(rows: ArrayLike<HTMLTableRowElement>): TableRowGroup[] {
    const groups: TableRowGroup[] = [];
    let groupStart = 0;
    let reach = -1;
    for (let i = 0; i < rows.length; i++) {
        Array.from(rows[i]?.cells ?? []).forEach((cell) => {
            reach = Math.max(reach, i + Math.max(cell.rowSpan || 1, 1) - 1);
        });
        reach = Math.max(reach, i);
        if (i === reach) {
            groups.push({ start: groupStart, end: i });
            groupStart = i + 1;
            reach = -1;
        }
    }
    return groups;
}

// ── Line decomposition (RECUR-27304 Delta v5) ────────────────────────────────
//
// Parallel to the table-row decomposition above: a long TEXT paragraph is
// split into one flat unit per rendered LINE instead of being pushed whole,
// so it flows across a page break like Word instead of leaving a page-sized
// gap. `measureBlockLines` is exported so `exportToPdfServer.ts`'s clone-split
// step can re-measure the SAME live block in the SAME (viewport) space its
// own caret-rect comparisons use, rather than reusing this module's
// NORMALIZED offsetTop values (which would be off by whatever gap is
// currently painted on the block — see this module's own coordinate-space
// note at the top of the file).

/** One measured visual line, in VIEWPORT (CSS px) coordinates. */
export interface MeasuredLine {
    top: number;
    bottom: number;
}

const LINE_RECT_TOP_TOLERANCE_PX = 1;

/**
 * Groups a Range's per-inline-run rects into visual LINES —
 * `Range.getClientRects()` returns one rect per inline run (a bold word, a
 * link, a plain text run, …), several of which share the SAME visual line.
 * An exact-`top` tolerance alone isn't enough: a line with MIXED inline
 * metrics (a bold word, a superscript, a link rendered in a different
 * font-size) yields rects whose `top`s genuinely differ by a few px even
 * though they're all on the same visual line — matched by exact tolerance
 * alone, those over-count as separate lines. A rect instead joins an
 * existing line when EITHER its `top` is within the small tolerance
 * (sub-pixel rounding between adjacent runs at the SAME size) OR its own
 * vertical midpoint falls inside that line's current `[top, bottom]` band
 * (catches a same-line run whose top/bottom sit at a different height
 * entirely). On join, the band widens to min(top)/max(bottom) across every
 * rect that landed in it — this can also pull a LATER rect's `top` closer to
 * an earlier one's, so band-overlap is re-checked against the (possibly
 * already-widened) candidate, not just the first rect that started it. A
 * degenerate (zero-area) rect — an empty text node, a collapsed range at a
 * boundary — is skipped. Sorted top-to-bottom (defensive: this editor's
 * plain LTR text flow already returns rects in that order, but
 * grouping-then-sorting doesn't depend on it).
 */
function groupRectsIntoLines(rects: ArrayLike<DOMRect>): MeasuredLine[] {
    const lines: MeasuredLine[] = [];
    Array.from(rects).forEach((rect) => {
        if (rect.width <= 0 && rect.height <= 0) return;
        const midY = (rect.top + rect.bottom) / 2;
        const line = lines.find(
            (candidate) =>
                Math.abs(candidate.top - rect.top) <= LINE_RECT_TOP_TOLERANCE_PX || (midY >= candidate.top && midY <= candidate.bottom),
        );
        if (line) {
            line.top = Math.min(line.top, rect.top);
            line.bottom = Math.max(line.bottom, rect.bottom);
        } else {
            lines.push({ top: rect.top, bottom: rect.bottom });
        }
    });
    return lines.sort((a, b) => a.top - b.top);
}

/**
 * Measures `block`'s rendered visual lines in VIEWPORT (CSS px) coordinates.
 * `[]` for an empty/whitespace-only block (no rects) or when Range
 * measurement is unavailable (defensive — mirrors the ResizeObserver guard
 * elsewhere in this codebase).
 */
export function measureBlockLines(block: HTMLElement): MeasuredLine[] {
    if (typeof document === 'undefined' || typeof document.createRange !== 'function') return [];
    const range = document.createRange();
    range.selectNodeContents(block);
    const rects = range.getClientRects ? range.getClientRects() : ([] as unknown as DOMRectList);
    return groupRectsIntoLines(rects);
}

/**
 * `measureBlockLines`, converted into the SAME (LIVE, gap-included)
 * offsetTop-space `buildAtomicUnits` already reasons in — mirrors
 * `cumulativeOffsetTop`'s coordinate-space conversion for table rows, via
 * `getBoundingClientRect` instead of the offsetParent chain (a plain
 * paragraph has no offsetParent quirk to work around, unlike a `<tr>`). NOT
 * yet gap-normalized — the caller applies the same per-block cumulative-gap
 * subtraction it already uses for the whole-block case.
 */
function measureLineOffsets(block: HTMLElement): MeasuredLine[] {
    const lines = measureBlockLines(block);
    if (lines.length === 0) return [];
    const blockViewportTop = block.getBoundingClientRect().top;
    return lines.map((line) => ({
        top: line.top - blockViewportTop + block.offsetTop,
        bottom: line.bottom - blockViewportTop + block.offsetTop,
    }));
}

// A cheap, CONSERVATIVE pre-filter so a document with many short (typically
// single-line) paragraphs doesn't force a Range/getClientRects measurement on
// every one of them on every recompute pass (this hook re-measures on every
// ResizeObserver-flagged reflow — see usePageBoundaries.ts). Deliberately well
// under the shortest plausible height of a genuinely-decomposable (>= 4-line)
// paragraph at this editor's smallest realistic line-height, so this can only
// ever skip a block that's obviously 1 line — never a false negative; a block
// that's merely 2-3 lines (also not decomposed — see LINE_ORPHAN_WIDOW_MIN_LINES)
// gets measured and correctly rejected either way, same outcome.
const MIN_HEIGHT_FOR_LINE_MEASUREMENT_PX = 32;

/**
 * Whether `block` is even a CANDIDATE for line decomposition — checked
 * BEFORE measurement, so ineligible blocks never pay for a Range/
 * getClientRects call at all.
 *
 * RECUR-27304 (font/DIV-parity investigation): WIDENED from a `tagName ===
 * 'P'` whitelist. Live inspection showed the One Pager editor renders EVERY
 * top-level block — including plain paragraphs — as a `DIV`
 * (`data-slate-node="element"`), never a literal `<p>`, so the old whitelist
 * silently never fired there at all (Delta v5 was dead code for the One
 * Pager). A tag whitelist is exactly the kind of thing that breaks again the
 * next time a block's underlying tag changes, so eligibility is now
 * STRUCTURAL (content-based) instead: a top-level `P` or `DIV` block that —
 *   • is not a table: `findTableElement` returns null (already decomposed
 *     into ROW units above when it has >=2 rows) AND it contains no nested
 *     `<table>` either (defensive — e.g. pasted HTML without the Slate
 *     table's own `data-slate-node` hook);
 *   • carries no media/structural descendant (`<img>`, `<figure>`, `<hr>`,
 *     `<iframe>`, `<video>`) — none of these flow as stacked reading lines,
 *     so `Range.getClientRects()` on one would return rects the grouping
 *     above was never built to interpret;
 *   • has non-empty visible text (an empty caret-landing `<p>`/`<div>` has
 *     nothing to measure lines from).
 * This deliberately still covers a list-item — `BlockList.tsx` wraps a list
 * block's OWN text in `<ul>/<li>`, but the block itself stays a top-level
 * `P`/`DIV` — the same as before, just no longer by accident of `<p>` being
 * the only tag ever whitelisted.
 *
 * The safeguards BELOW this check are what make trusting a wider structural
 * predicate safe rather than needing an exhaustive tag blacklist (headings,
 * blockquote, callout, code-block, TOC, column-group, …): a short block
 * (heading, one-line callout) never clears `MIN_HEIGHT_FOR_LINE_MEASUREMENT_PX`
 * or `LINE_ORPHAN_WIDOW_MIN_LINES` below, and a structured block whose
 * `Range.getClientRects()` doesn't measure as expected (or returns nothing —
 * `measureBlockLines`'s empty-result case) falls back to the pre-existing
 * whole-block push, exactly as every non-candidate already does today.
 *
 * Shared by BOTH the One Pager and the Reports editor (`PlateDocumentDrawer`
 * renders the SAME `PlateEditor` — no per-surface markup fork), so this
 * widening applies identically to both; nothing here is One-Pager-specific.
 */
function isLineDecomposableBlock(block: HTMLElement): boolean {
    if (block.tagName !== 'P' && block.tagName !== 'DIV') return false;
    if (findTableElement(block) !== null) return false;
    if (block.querySelector('table, img, figure, hr, iframe, video')) return false;
    return (block.textContent || '').trim().length > 0;
}

/**
 * Per-block unit-building result: `geometry` is the FLAT atomic-unit list fed
 * to `pageBreakModel`, in NORMALIZED (gap-free) coordinates — one entry per
 * whole block, per rowspan-bound row GROUP for a decomposed table
 * (RECUR-27304 rowspan-safe pagination — ordinarily one `<tr>` each, see
 * `computeRowGroups`), or per rendered LINE for a decomposed paragraph
 * (RECUR-27304 Delta v5); `liveOffsetTop` is the SAME flat list's CURRENT
 * on-screen offsetTop (gap included, whatever is actually painted right
 * now), used only to project a model decision back onto the live DOM for the
 * overlay. `rowCounts`/`lineCounts`/`unitStart` are parallel to the ORIGINAL
 * `blocks` array and are exactly what `reindexGroupsForRowsAndLines` needs to
 * re-map `buildPaginationGroups`' block-level groups onto `geometry`'s
 * indices — `rowCounts` now counts row GROUPS, not literal `<tr>`s, but
 * `reindexGroupsForRowsAndLines` needs no change for that: it only ever
 * reasons about a COUNT of flat units, never what a unit represents.
 * `rowGroups` is parallel to `blocks` too (`[]` for a non-table/
 * non-decomposed block) — `buildUnitMeta` needs the actual group boundaries
 * to resolve each row-kind unit's live `<tr>` and `rowIndex`.
 */
function buildAtomicUnits(blocks: HTMLElement[]): {
    geometry: BlockGeometry[];
    liveOffsetTop: number[];
    rowCounts: number[];
    rowGroups: TableRowGroup[][];
    lineCounts: number[];
    unitStart: number[];
} {
    const geometry: BlockGeometry[] = [];
    const liveOffsetTop: number[] = [];
    const rowCounts: number[] = [];
    const rowGroups: TableRowGroup[][] = [];
    const lineCounts: number[] = [];
    const unitStart: number[] = [];

    // Cumulative gap (CSS px) currently painted at-and-above each top-level
    // block (RECUR-27304 gap-display), read back from the DOM rather than
    // tracked as separate state — see PAGE_GAP_CSS_VAR's doc comment.
    // INCLUSIVE of the block's own gap: a block's own margin-top shifts its
    // OWN offsetTop down too, not just its siblings' below it.
    let cumulativeGap = 0;
    const gapAboveAndIncluding = blocks.map((block) => {
        cumulativeGap += getInjectedGapPx(block);
        return cumulativeGap;
    });

    blocks.forEach((block, blockIdx) => {
        unitStart.push(geometry.length);
        const liveTop = block.offsetTop;
        // NORMALIZED: what this block's offsetTop would be with NO gap ever
        // applied — the only space `pageBreakModel` is allowed to see, so
        // re-running the pipeline against already-gapped geometry reproduces
        // the SAME decision instead of oscillating (RECUR-27304
        // measurement-feedback-loop fix).
        const normalizedTop = liveTop - gapAboveAndIncluding[blockIdx];
        const table = findTableElement(block);
        // table.rows (not a recursive querySelectorAll) matches only THIS
        // table's own rows — never a nested table's, if one is ever nested
        // inside a cell — and is the same API exportHelpers.ts's
        // measureRenderedColumnWidths already uses for table rows.
        const rows = table ? Array.from(table.rows) : [];
        // RECUR-27304 rowspan-safe pagination: decompose by rowspan-bound
        // GROUP (computeRowGroups), not by literal `<tr>` — see this file's
        // "Table row-group decomposition" section above. A table with no
        // rowSpan anywhere yields one group per row, so `tableRowGroups`
        // below is byte-identical to `rows` itself for every plain table.
        const tableRowGroups = table && rows.length >= 2 ? computeRowGroups(table.rows) : [];
        if (tableRowGroups.length >= 2) {
            rowCounts.push(tableRowGroups.length);
            rowGroups.push(tableRowGroups);
            lineCounts.push(0);
            tableRowGroups.forEach((rowGroup) => {
                const firstRow = rows[rowGroup.start];
                const lastRow = rows[rowGroup.end];
                // Rows are never independently gapped (see PageGapTarget's
                // doc comment) — the same block-level delta applies to every
                // row. Height spans the FULL group (last row's bottom minus
                // first row's top), not just the first row's own height, so
                // a tall rowspan cell's height is attributed to the whole
                // GROUP instead of silently truncated to its origin `<tr>`
                // alone (RECUR-27304 rowspan-safe pagination).
                const groupTopWithinTable = cumulativeOffsetTop(firstRow, table as HTMLTableElement);
                const groupBottomWithinTable = cumulativeOffsetTop(lastRow, table as HTMLTableElement) + lastRow.offsetHeight;
                geometry.push({
                    offsetTop: normalizedTop + groupTopWithinTable,
                    offsetHeight: groupBottomWithinTable - groupTopWithinTable,
                });
                liveOffsetTop.push(liveTop + groupTopWithinTable);
            });
            return;
        }
        rowCounts.push(0);
        rowGroups.push([]);

        // RECUR-27304 Delta v5: attempt line decomposition for an eligible,
        // tall-enough paragraph before falling back to a single whole-block
        // unit — mirrors the table branch above, one flat unit per rendered
        // LINE instead of per `<tr>`.
        const liveLines =
            isLineDecomposableBlock(block) && block.offsetHeight > MIN_HEIGHT_FOR_LINE_MEASUREMENT_PX ? measureLineOffsets(block) : [];
        if (liveLines.length >= LINE_ORPHAN_WIDOW_MIN_LINES) {
            lineCounts.push(liveLines.length);
            liveLines.forEach((line) => {
                geometry.push({ offsetTop: line.top - gapAboveAndIncluding[blockIdx], offsetHeight: line.bottom - line.top });
                liveOffsetTop.push(line.top);
            });
            return;
        }

        // Not decomposable either way (no table; wrong block type, too
        // short, or fewer than LINE_ORPHAN_WIDOW_MIN_LINES lines) — one
        // whole-block unit, unchanged from today.
        lineCounts.push(0);
        geometry.push({ offsetTop: normalizedTop, offsetHeight: block.offsetHeight });
        liveOffsetTop.push(liveTop);
    });

    return { geometry, liveOffsetTop, rowCounts, rowGroups, lineCounts, unitStart };
}

/** Parallel to `geometry` — the live element/kind/table/paragraph metadata for one flat unit. */
interface UnitMeta {
    element: HTMLElement;
    kind: 'block' | 'row' | 'line';
    tableBlock?: HTMLElement;
    rowIndex?: number;
    textBlock?: HTMLElement;
    lineIndex?: number;
    /** Position in `getTopLevelBlocks(contentEl)` — set only for `kind === 'block'`. */
    blockIndex?: number;
}

/**
 * Builds `unitMeta` in lockstep with `buildAtomicUnits`' `geometry` (same
 * block iteration, same row-group/line expansion) so `unitMeta[i]` always
 * describes the same flat unit as `geometry[i]`. Row-GROUP 0 of a decomposed
 * table (RECUR-27304 rowspan-safe pagination — ordinarily just row 0, see
 * `computeRowGroups`) — and, likewise, LINE 0 of a decomposed paragraph
 * (RECUR-27304 Delta v5) — is a `'block'`-kind unit whose `element` is the
 * table's/paragraph's own top-level block — `reindexGroupsForRowsAndLines`
 * never lets row/line 0 start its OWN group (it's always folded into the
 * preceding sticky group, or stands alone as the merged first group when
 * there's no preceding heading), so if THIS flat index is ever chosen as a
 * page-start it means "the whole table/paragraph (or its heading) begins the
 * page" — nothing to split, matching `'block'` kind. Every later row GROUP is
 * independently breakable (`'row'` kind, `rowIndex` set to the group's own
 * FIRST `<tr>` index — the only row a page-break may ever land on, per
 * `TableRowGroup`); every later line likewise (`'line'` kind, `lineIndex` >= 1
 * — though by construction of the orphan/widow grouping, `lineIndex` 1
 * specifically can never actually be SELECTED as a page-start; see
 * `reindexGroupsForRowsAndLines`).
 */
function buildUnitMeta(blocks: HTMLElement[], rowCounts: number[], rowGroups: TableRowGroup[][], lineCounts: number[]): UnitMeta[] {
    const unitMeta: UnitMeta[] = [];
    blocks.forEach((block, blockIdx) => {
        const groupCount = rowCounts[blockIdx] ?? 0;
        if (groupCount >= 2) {
            const table = findTableElement(block) as HTMLTableElement; // guaranteed non-null: groupCount>=2 came from computeRowGroups(table.rows) above
            const tableRowGroups = rowGroups[blockIdx];
            for (let g = 0; g < groupCount; g++) {
                unitMeta.push(
                    g === 0
                        ? { element: block, kind: 'block', blockIndex: blockIdx }
                        : {
                              element: table.rows[tableRowGroups[g].start],
                              kind: 'row',
                              tableBlock: block,
                              rowIndex: tableRowGroups[g].start,
                          },
                );
            }
            return;
        }

        const lines = lineCounts[blockIdx] ?? 0;
        if (lines >= LINE_ORPHAN_WIDOW_MIN_LINES) {
            for (let l = 0; l < lines; l++) {
                unitMeta.push(
                    l === 0
                        ? { element: block, kind: 'block', blockIndex: blockIdx }
                        : { element: block, kind: 'line', textBlock: block, lineIndex: l },
                );
            }
            return;
        }

        unitMeta.push({ element: block, kind: 'block', blockIndex: blockIdx });
    });
    return unitMeta;
}

/** Shared core result: everything both public projections need. */
interface PageStartCore {
    pageHeightPx: number;
    /** NORMALIZED (gap-free) geometry — see module doc's coordinate-space note. */
    geometry: BlockGeometry[];
    /** Parallel to `geometry` — the SAME units' LIVE (post-gap) offsetTop. */
    liveOffsetTop: number[];
    unitMeta: UnitMeta[];
    startUnitIndices: number[];
    /** Parallel to `startUnitIndices` — the model's raw `unusedPx` per break (RECUR-27304 gap-display), before any kind-based filtering for what actually gets painted. */
    startGaps: number[];
}

/**
 * Shared private core (RECUR-27304 Delta v3): given the editor's top-level
 * blocks and the measured content width, builds the same sticky groups /
 * manual-break flags / row-decomposition the exporter uses, runs the pure
 * `pageBreakModel` fit-loop against the SERVER page height, and returns the
 * page-start UNIT INDICES alongside the flat geometry/metadata needed to
 * project them. `null` when the width isn't measurable (mirrors the previous
 * `EMPTY_BOUNDARIES` early-out).
 */
function computePageStartIndices(blocks: HTMLElement[], contentWidthPx: number): PageStartCore | null {
    if (!Number.isFinite(contentWidthPx) || contentWidthPx <= 0) return null;

    const pageHeightPx = getServerPageHeightPx(contentWidthPx);
    if (!Number.isFinite(pageHeightPx) || pageHeightPx <= 0 || blocks.length === 0) {
        return { pageHeightPx, geometry: [], liveOffsetTop: [], unitMeta: [], startUnitIndices: [], startGaps: [] };
    }

    // buildPaginationGroups classifies each block by its OWN tagName (H1-H6
    // sticky; TABLE/HR/IMG/FIGURE/UL/OL group-terminating). A table's tagName
    // lives on the <table> one level inside its `.tableScroll` wrapper (see
    // findTableElement) — passing the wrapper itself would leave
    // buildPaginationGroups blind to it (GROUP_END_TAGS never matches a bare
    // `DIV`), letting a sticky heading's lookahead sweep straight past the
    // table into whatever follows it instead of stopping there. Classify off
    // the inner <table> instead; every other block is unchanged.
    const classificationBlocks = blocks.map((block) => findTableElement(block) ?? block);
    const groups = buildPaginationGroups(classificationBlocks);

    // RECUR-27304 Delta v2/v3/v5: decompose any table block into one atomic
    // unit per rowspan-bound row GROUP (ordinarily one per row — see
    // computeRowGroups), and any long-enough plain paragraph into one atomic
    // unit per rendered LINE, then re-map the block-level groups above onto
    // that flat list — see buildAtomicUnits / reindexGroupsForRowsAndLines
    // for why this must be ONE combined pass.
    const { geometry, liveOffsetTop, rowCounts, rowGroups, lineCounts, unitStart } = buildAtomicUnits(blocks);
    const reindexedGroups = reindexGroupsForRowsAndLines(groups, rowCounts, lineCounts, unitStart);

    // `geometry` is NORMALIZED (gap-free — see module doc), so this always
    // runs against the SAME layout regardless of whatever gap is currently
    // painted on the live DOM (RECUR-27304 gap-display) — this is what keeps
    // `computePageStartUnits` byte-identical whether or not `showPageGaps`
    // happens to be on at export time.
    const pageBreaks = pageBreakModelWithGaps(reindexedGroups, geometry, pageHeightPx);
    const unitMeta = buildUnitMeta(blocks, rowCounts, rowGroups, lineCounts);

    return {
        pageHeightPx,
        geometry,
        liveOffsetTop,
        unitMeta,
        startUnitIndices: pageBreaks.map((gap) => gap.startIdx),
        startGaps: pageBreaks.map((gap) => gap.unusedPx),
    };
}

/**
 * Glue, exported standalone so the core calc is unit-testable without a
 * ResizeObserver: given the editor's top-level block elements and the
 * measured content width, returns the overlay's boundary Ys — byte-identical
 * shape/values to the pre-Delta-v3 `usePageBoundaries.computePageBoundaries`,
 * now paired with the per-boundary gap (RECUR-27304 gap-display).
 * `boundaries` are LIVE (post-gap) coordinates — see module doc.
 */
export function computePageBoundaries(blocks: HTMLElement[], contentWidthPx: number): PageBoundaries {
    const core = computePageStartIndices(blocks, contentWidthPx);
    if (!core) return EMPTY_BOUNDARIES;
    return {
        boundaries: core.startUnitIndices.map((idx) => core.liveOffsetTop[idx]),
        // Only a 'block'-kind start ever gets a real DOM gap (see
        // computePageGapTargets — margin has no effect on a <tr>), so a
        // mid-table 'row' start reports 0 here even though the model's own
        // unusedPx is exact — nothing is actually rendered there to paint.
        gaps: core.startUnitIndices.map((idx, i) => (core.unitMeta[idx].kind === 'block' ? core.startGaps[i] : 0)),
        kinds: core.startUnitIndices.map((idx) => core.unitMeta[idx].kind),
        pageHeightPx: core.pageHeightPx,
    };
}

/**
 * The export-time projection (RECUR-27304 Delta v3): finds `contentEl`'s
 * top-level blocks itself (same selector/filter the overlay hook uses),
 * measures its own width, and maps each model page-start unit index back onto
 * the LIVE element(s) it corresponds to — see `PageStartUnit`. Returns `[]`
 * when the width isn't measurable (mirrors `computePageBoundaries`).
 *
 * Unaffected by gap application (RECUR-27304 gap-display): `core.geometry` is
 * NORMALIZED, so `offsetTop` here is the same gap-free value this function
 * has always returned, regardless of whether `showPageGaps` gaps happen to
 * be painted on `contentEl` right now — see the module doc's coordinate-space
 * note. The exporter itself is untouched.
 */
export function computePageStartUnits(contentEl: HTMLElement): PageStartUnit[] {
    const blocks = getTopLevelBlocks(contentEl);
    const core = computePageStartIndices(blocks, contentEl.offsetWidth);
    if (!core) return [];
    return core.startUnitIndices.map((idx) => ({ ...core.unitMeta[idx], offsetTop: core.geometry[idx].offsetTop }));
}

/**
 * Gap-application targets for the live editor DOM (RECUR-27304 gap-display):
 * every 'block'-kind page-start paired with the vertical space to render
 * immediately above it — see `PageGapTarget`. Consumed by
 * `usePageBoundaries`'s `applyPageGapStyles`. Recomputes the shared core
 * independently of `computePageBoundaries` (same pattern already used by
 * `computePageStartUnits` above) — cheap: no gap has been applied between
 * the two calls in the same measurement pass, so the second DOM read hits
 * the browser's already-current layout, no extra reflow.
 */
export function computePageGapTargets(blocks: HTMLElement[], contentWidthPx: number): PageGapTarget[] {
    const core = computePageStartIndices(blocks, contentWidthPx);
    if (!core) return [];
    const targets: PageGapTarget[] = [];
    core.startUnitIndices.forEach((idx, i) => {
        const meta = core.unitMeta[idx];
        if (meta.kind !== 'block') return; // margin-top has no effect on a <tr> — see PageGapTarget's doc comment.
        targets.push({ element: meta.element, gapPx: core.startGaps[i] });
    });
    return targets;
}
